import React, { useState, useCallback } from "react";
import { formatUah, formatProcent } from "utils";
import { noImage, cashIcon, debitIcon } from "assets";
import { InfoModal, PermissionWrapper, Dropdown, DeleteButton } from "components";

// Мапа id типів цін на іконки
const propertyIcons = {
  1: { src: cashIcon, alt: "cashIcon", title: "Готівка" },
  2: { src: debitIcon, alt: "debitIcon", title: "Безготівка" },
};

// Опції для випадаючого списку наявності
const AVAILABILITY_OPTIONS = [
  { value: 1, label: "В наявності" },
  { value: 0, label: "Немає" },
];

// Компонент для відображення одного рядка в таблиці продукції партнерів
const PartnerRow = ({ item, onImageClick, onUpdate, onDelete }) => {
  // Локальний стан для керування режимом редагування
  const [editing, setEditing] = useState({ field: null, value: "" });

  // Функція для виходу з режиму редагування
  const cancelEditing = useCallback(() => {
    setEditing({ field: null, value: "" });
  }, []);

  // Універсальна функція для надсилання оновлень
  const handleUpdate = (field, newValue) => {
    let finalValue = newValue;

    // Спеціальна обробка для ціни
    if (field === "price") {
      const numericValue = parseFloat(newValue);
      // Перевіряємо, чи є значення валідним числом
      if (isNaN(numericValue) || numericValue < 0) {
        cancelEditing();
        return;
      }
      finalValue = numericValue;
    }

    // Запобігаємо зайвим запитам, якщо значення не змінилося
    if (String(item[field]) === String(finalValue)) {
      cancelEditing();
      return;
    }

    // Викликаємо функцію оновлення зі стору
    onUpdate(item.article, { [field]: finalValue });
    cancelEditing();
  };

  // Обробник натискання клавіш (Enter/Escape)
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleUpdate(editing.field, editing.value);
    } else if (e.key === "Escape") {
      cancelEditing();
    }
  };

  // Обробник зміни типу оплати
  const handlePropertyToggle = () => {
    const newPropertyId = item.property_id === 1 ? 2 : 1;
    onUpdate(item.article, { property_id: newPropertyId });
  };

  // Дані для модального вікна
  const margin = item.price - item.purchase_price;
  const marginPercent = item.price > 0 ? margin / item.price : 0;
  const modalInfo = [
    { label: "Закупівельна ціна", value: formatUah(item.purchase_price) },
    { label: "Маржа", value: formatUah(margin) },
    { label: "Відсоток маржі", value: formatProcent(marginPercent) },
  ];

  const iconProps = propertyIcons[item.property_id];

  return (
    <tr className={!item.is_active ? "inactive-row" : ""}>
      {/* Зображення товару */}
      <td>
        <img
          src={item.img_url || noImage}
          alt={item.name}
          className={`${item.img_url ? "clickable" : ""}`}
          onClick={() => onImageClick(item.img_url)}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = noImage;
          }}
        />
      </td>

      {/* Артикул */}
      <td>{item.article}</td>

      {/* Назва та модель */}
      <td>
        {item.name}
        {item.model && <span className="model">{item.model}</span>}
      </td>

      {/* Тип ціни */}
      <PermissionWrapper
        requiredPermission="partners:edit"
        fallback={
          iconProps && (
            <td>
              <img
                className="icon"
                src={iconProps.src}
                alt={iconProps.alt}
                title={iconProps.title}
              />
            </td>
          )
        }
      >
        <td onDoubleClick={handlePropertyToggle}>
          {iconProps && (
            <img
              className="icon clickable"
              src={iconProps.src}
              alt={iconProps.alt}
              title={iconProps.title}
            />
          )}
        </td>
      </PermissionWrapper>

      {/* Ціна */}
      <PermissionWrapper
        requiredPermission="partners:edit"
        fallback={
          <td>
            <PermissionWrapper
              requiredPermission="partners:view-stocks"
              fallback={<span>{formatUah(item.price)}</span>}
            >
              <InfoModal info={modalInfo}>
                <span>{formatUah(item.price)}</span>
              </InfoModal>
            </PermissionWrapper>
          </td>
        }
      >
        <td onDoubleClick={() => setEditing({ field: "price", value: item.price })}>
          {editing.field === "price" ? (
            <input
              type="number"
              value={editing.value}
              onChange={(e) =>
                setEditing((prev) => ({ ...prev, value: e.target.value }))
              }
              onBlur={() => handleUpdate("price", editing.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
          ) : (
            <PermissionWrapper
              requiredPermission="partners:view-stocks"
              fallback={<span>{formatUah(item.price)}</span>}
            >
              <InfoModal info={modalInfo}>
                <span>{formatUah(item.price)}</span>
              </InfoModal>
            </PermissionWrapper>
          )}
        </td>
      </PermissionWrapper>

      {/* Наявність */}
      <PermissionWrapper
        requiredPermission="partners:edit"
        fallback={
          <td className={`availability-status ${item.availability}`}>
            {item.availability ? "В наявності" : "Немає"}
          </td>
        }
      >
        <td
          onDoubleClick={() =>
            setEditing({ field: "availability", value: item.availability })
          }
        >
          {editing.field === "availability" ? (
            <Dropdown
              options={AVAILABILITY_OPTIONS}
              selected={editing.value}
              onChange={(selectedValue) => handleUpdate("availability", selectedValue)}
              mode="select"
              autoFocus
            />
          ) : (
            <span className={`availability-status ${item.availability}`}>
              {item.availability ? "В наявності" : "Немає"}
            </span>
          )}
        </td>
      </PermissionWrapper>

      {/* Дії */}
      <PermissionWrapper requiredPermission={["partners:edit", "partners:delete"]}>
        <td>
          <div className="cell-container">
            <PermissionWrapper requiredPermission="partners:delete">
              {item.is_castom && (
                <DeleteButton onDelete={() => onDelete(item.article)} />
              )}
            </PermissionWrapper>
            <PermissionWrapper requiredPermission="partners:edit">
              <input
                className="focus-checkbox"
                type="checkbox"
                checked={item.is_active}
                onChange={(e) =>
                  onUpdate(item.article, { is_active: e.target.checked })
                }
                title={item.is_active ? "Вимкнути" : "Увімкнути"}
              />
            </PermissionWrapper>
          </div>
        </td>
      </PermissionWrapper>
    </tr>
  );
};

export default PartnerRow;
